import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import RadioSliderField from 'components/RadioSliderField';
import {
  TABLE_EXPORT_COLUMNS_SCOPE,
  TABLE_EXPORT_FORMAT,
  TABLE_EXPORT_ROWS,
  TableExportSelection,
} from 'models/TableExport';
import utils from 'utils';
import { SIZE } from 'models/Size';

import './TableExportForm.styles.scss';

type TableExportFormProps = {
  className?: string;
  totalItems: number;
  pageItemsCount: number;
  totalPages: number;
  totalColumnsCount: number;
  visibleColumnsCount: number;
  isExporting?: boolean;
  onExport: (selection: TableExportSelection) => Promise<void> | void;
  onCancel: () => void;
};

const TableExportForm: React.FC<TableExportFormProps> = (props) => {
  const {
    className,
    totalItems,
    pageItemsCount,
    totalPages,
    totalColumnsCount,
    visibleColumnsCount,
    isExporting,
    onExport,
    onCancel,
  } = props;

  const { t } = useTranslation();

  const classes = classNames('table-export-form', className);

  const hasHiddenColumns = visibleColumnsCount < totalColumnsCount;

  const hasSinglePage = totalPages <= 1;

  const rowsOptions = useMemo(
    () => [
      { value: TABLE_EXPORT_ROWS.ALL, label: t('allResults') },
      { value: TABLE_EXPORT_ROWS.PAGE, label: t('thisPage') },
    ],
    [t],
  );

  const columnsOptions = useMemo(
    () => [
      { value: TABLE_EXPORT_COLUMNS_SCOPE.VISIBLE, label: t('visible') },
      { value: TABLE_EXPORT_COLUMNS_SCOPE.ALL, label: t('all') },
    ],
    [t],
  );

  const formatOptions = useMemo(
    () => [
      { value: TABLE_EXPORT_FORMAT.CSV, label: t('csv') },
      { value: TABLE_EXPORT_FORMAT.XLSX, label: t('xlsx') },
    ],
    [t],
  );

  const rowsField = useMemo(
    () =>
      !hasSinglePage && (
        <Field
          name="rows"
          label={t('rows')}
          slideSteps={rowsOptions}
          component={RadioSliderField}
        />
      ),
    [hasSinglePage, rowsOptions, t],
  );

  const formatField = useMemo(
    () => (
      <Field
        name="format"
        label={t('format')}
        slideSteps={formatOptions}
        component={RadioSliderField}
      />
    ),
    [formatOptions, t],
  );

  const initialValues = useMemo(
    () => ({
      rows: TABLE_EXPORT_ROWS.ALL,
      columns: TABLE_EXPORT_COLUMNS_SCOPE.VISIBLE,
      format: TABLE_EXPORT_FORMAT.CSV,
    }),
    [],
  );

  const onSubmit = useCallback(
    async ({ rows, columns, format }: TableExportSelection) => {
      try {
        await onExport({
          rows,
          columns: hasHiddenColumns ? columns : TABLE_EXPORT_COLUMNS_SCOPE.ALL,
          format,
        });

        onCancel();
      } catch (e) {
        return;
      }
    },
    [onExport, onCancel, hasHiddenColumns],
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, submitting, values }) => {
        const rowsCount =
          values.rows === TABLE_EXPORT_ROWS.ALL ? totalItems : pageItemsCount;

        const columnsCount =
          !hasHiddenColumns || values.columns === TABLE_EXPORT_COLUMNS_SCOPE.ALL
            ? totalColumnsCount
            : visibleColumnsCount;

        return (
          <form onSubmit={handleSubmit} className={classes}>
            {hasHiddenColumns ? (
              <>
                {rowsField && (
                  <div className="table-export-form__row">{rowsField}</div>
                )}

                <div className="table-export-form__row">
                  <Field
                    name="columns"
                    label={t('columns')}
                    slideSteps={columnsOptions}
                    component={RadioSliderField}
                  />
                  {formatField}
                </div>
              </>
            ) : (
              <div className="table-export-form__row">
                {rowsField}
                {formatField}
              </div>
            )}

            <div className="table-export-form__summary">
              <p>
                {t(
                  columnsCount === totalColumnsCount
                    ? 'tableExportSummaryAllColumns'
                    : 'tableExportSummary',
                  {
                    rows: utils.getDotedNumber(rowsCount),
                    rowsLabel: t('row', { count: rowsCount }),
                    columns: utils.getDotedNumber(columnsCount),
                    columnsLabel: t('column', { count: columnsCount }),
                  },
                )}
              </p>
              <p>{t('tableExportFiltersApplied')}</p>
            </div>

            <div className="actions">
              <Button variant="link" onClick={onCancel} disabled={submitting}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={submitting || isExporting}>
                {t('export')}
                {(submitting || isExporting) && (
                  <Loader size={SIZE.SMALL} inline overlay={false} />
                )}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default TableExportForm;
